// Thin fetch wrapper. Auth rides on the same signed session cookie the Rails
// pages use, so every mutating request carries the CSRF token Rails put in the
// page head.

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}

class ApiError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

async function request(path, { method = 'GET', body } = {}) {
  const headers = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const token = csrfToken()
  if (token) headers['X-CSRF-Token'] = token

  const response = await fetch(`/api/v1${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (response.status === 204) return null

  const text = await response.text()
  const payload = text ? JSON.parse(text) : null

  if (!response.ok) {
    throw new ApiError(payload?.error || `Request failed (${response.status})`, response.status)
  }

  return payload
}

export const api = {
  session: () => request('/session'),
  signIn: (email_address, password) =>
    request('/session', { method: 'POST', body: { email_address, password } }),
  signUp: (email_address, password) =>
    request('/registration', { method: 'POST', body: { email_address, password } }),
  signOut: () => request('/session', { method: 'DELETE' }),

  platformAccounts: () => request('/platform_accounts'),
  linkSleeper: (username) =>
    request('/platform_accounts', { method: 'POST', body: { username } }),
  availableLeagues: (accountId) => request(`/platform_accounts/${accountId}/leagues`),

  leagues: () => request('/leagues'),
  league: (id) => request(`/leagues/${id}`),
  connectLeague: (platform_account_id, external_id) =>
    request('/leagues/connect', { method: 'POST', body: { platform_account_id, external_id } }),
  syncLeague: (id) => request(`/leagues/${id}/sync`, { method: 'POST' }),

  leaguePlayers: (leagueId, { teamId, q } = {}) => {
    const params = new URLSearchParams()
    if (teamId) params.set('team_id', teamId)
    if (q) params.set('q', q)
    const query = params.toString()
    return request(`/leagues/${leagueId}/players${query ? `?${query}` : ''}`)
  },

  createTrade: (league_id, assets, { exploratory } = {}) =>
    request('/trades', { method: 'POST', body: { league_id, assets, exploratory } }),
  trade: (id) => request(`/trades/${id}`),
  evaluation: (id) => request(`/trades/${id}/evaluation`),

  trades: () => request('/trades'),
  saveTrade: (id, name) => request(`/trades/${id}`, { method: 'PATCH', body: { name } }),
  deleteTrade: (id) => request(`/trades/${id}`, { method: 'DELETE' }),

  shareTrade: (id) => request(`/trades/${id}/share`, { method: 'POST' }),
  unshareTrade: (id) => request(`/trades/${id}/share`, { method: 'DELETE' }),
  sharedTrade: (token) => request(`/shared/${token}`),
}

export { ApiError }
